// 影片檔案副檔名列表
const VIDEO_EXTENSIONS = [
  ".mp4",
  ".avi",
  ".mov",
  ".wmv",
  ".flv",
  ".webm",
  ".mkv",
  ".m4v",
  ".3gp",
  ".ogv",
  ".ts",
  ".mts",
  ".m2ts",
];

/**
 * 解析 YouTube URL 並提取影片 ID
 * @param {string} url - YouTube URL
 * @returns {string|null} - YouTube 影片 ID，如果無法解析則返回 null
 */
export const parseYouTubeId = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) {
      return u.pathname.replace("/", "");
    }
    if (u.hostname.includes("youtube.com")) {
      if (u.pathname === "/watch") {
        return u.searchParams.get("v");
      }
      const parts = u.pathname.split("/");
      const idx = parts.indexOf("embed");
      if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
    }
  } catch (e) {
    // ignore
  }
  const m = url.match(/(?:youtu\.be\/|v=|embed\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : null;
};

/**
 * 判斷 URL 是否為 YouTube 連結
 * @param {string} url - URL 字串
 * @returns {boolean} - 是否為 YouTube 連結
 */
export const isYouTubeUrl = (url) => {
  if (!url) return false;
  return url.includes("youtube.com") || url.includes("youtu.be");
};

/**
 * 判斷檔案是否為影片格式
 * @param {string} filename - 檔案名稱或URL
 * @returns {boolean} - 是否為影片格式
 */
export const isVideoFile = (filename) => {
  if (!filename) return false;

  const extension = filename.toLowerCase().split(".").pop();
  return VIDEO_EXTENSIONS.includes(`.${extension}`);
};

/**
 * 根據檔案類型渲染媒體元素
 * @param {string} src - 媒體檔案來源
 * @param {string} alt - 替代文字
 * @param {string} className - CSS類別
 * @param {object} videoProps - 影片額外屬性
 * @param {boolean} allowFullscreen - 是否允許雙擊全螢幕，預設為 false
 * @param {boolean} allowInteraction - 是否允許影片互動，預設為 true
 * @param {function} onImageClick - 圖片點擊事件處理器
 * @param {boolean} allowImageClick - 是否允許圖片點擊，預設為 true
 * @returns {JSX.Element} - 圖片、影片或 YouTube iframe 元素
 */
export const renderMedia = (
  src,
  alt = "",
  className = "",
  videoProps = {},
  allowFullscreen = false,
  allowInteraction = true,
  onImageClick = null,
  allowImageClick = true
) => {
  // 檢查是否為 YouTube 連結
  if (isYouTubeUrl(src)) {
    const id = parseYouTubeId(src);
    const base = id ? `https://www.youtube.com/embed/${id}` : src;
    const origin =
      typeof window !== "undefined" && window.location && window.location.origin
        ? `&origin=${encodeURIComponent(window.location.origin)}`
        : "";
    // 啟用 JS API 以便透過 postMessage 控制播放/暫停
    const embedUrl = `${base}?rel=0&playsinline=1&enablejsapi=1${origin}`;
    const finalClassName = allowInteraction ? className : `${className} pointer-events-none`;
    
    return (
      <iframe
        title={alt || "YouTube 影片"}
        className={finalClassName}
        src={embedUrl}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        style={{ width: "100%", height: "100%" }}
      />
    );
  }

  if (isVideoFile(src)) {
    const handleDoubleClick = (e) => {
      if (!allowFullscreen) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // 如果不允許互動，添加 pointer-events: none 樣式
    const finalClassName = allowInteraction ? className : `${className} pointer-events-none`;

    return (
      <video
        src={src}
        className={finalClassName}
        controls={allowInteraction}
        preload="metadata"
        onDoubleClick={handleDoubleClick}
        {...videoProps}
      >
        您的瀏覽器不支援影片播放。
      </video>
    );
  }

  const shouldAllowClick = allowImageClick && onImageClick;
  const imageClassName = `${className} ${shouldAllowClick ? "cursor-pointer hover:opacity-90 transition-opacity" : "pointer-events-none"}`;

  return (
    <img
      src={src}
      alt={alt}
      className={imageClassName}
      loading="lazy"
      decoding="async"
      onClick={shouldAllowClick ? onImageClick : undefined}
    />
  );
};
